import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { formatDateToCustomReadable, useAuth, useIsMobile } from '@repo/common';
import { ReactComponent as ReceiptIcon } from '@repo/common/assets/icons/20px/icon_20px_receipt_outline.svg';
import { ReactComponent as MobileReceiptIcon } from '@repo/common/assets/icons/16px/icon_16px_receipt_outline.svg';
import { ReactComponent as ReceiptCancelIcon } from '@repo/common/assets/icons/20px/icon_20px_receipt-cancel_outline.svg';
import { ReactComponent as MobileReceiptCancelIcon } from '@repo/common/assets/icons/16px/icon_16px_receipt-cancel_outline.svg';

const BillingRow = ({ data, invoiceData }) => {
  const [searchParams] = useSearchParams();
  const invoiceNumber = searchParams.get('invoice');
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const isAdmin = user?.role === 'A';

  const status = data?.status;

  const itemsArray =
    data.items
      ?.replace(/^워크샵 예약 -\s*/, '')
      ?.split(',')
      ?.map((item) => item.trim())
      ?.filter(Boolean) ?? [];

  const affectedCount = itemsArray.length;
  const totalCount = invoiceData?.workshopIds?.length ?? affectedCount;

  const scope =
    totalCount <= 1
      ? 'single'
      : affectedCount >= totalCount
        ? 'all'
        : 'partial';

  const renderItemsText = () => {
    if (status === 'ITEM_CANCELED') {
      return itemsArray.length > 0
        ? itemsArray.map((item) => `[Workshop] ${item}`).join(', ')
        : isMobile
          ? null
          : '-';
    }

    if (status === 'PARTIALLY_PAID') {
      const names = getItemNamesByStatuses(['CONFIRMED', 'ATTENDED', 'ABSENT']);
      return names || (isMobile ? null : '-');
    }

    if (scope === 'single') {
      return isMobile ? null : '-';
    }
    if (scope === 'all') {
      return 'All items';
    }
    return itemsArray.map((item) => `[Workshop] ${item}`).join(', ');
  };

  const itemsText = renderItemsText();

  const getCanceledItemLabel = () => {
    const serials = invoiceData?.canceledSerialNumbers ?? [];
    const idx = serials.findIndex((s) => s === data.canceledSerialNumber);
    if (idx >= 0) {
      return `Item ${String(idx + 1).padStart(2, '0')}`;
    }
    return 'Item';
  };

  const formattedStatus =
    status === 'INVOICE_CREATED' ? (
      <>
        Invoice <span className='text-green-deep03'>{invoiceNumber}</span> was
        created
      </>
    ) : status === 'PAYMENT_COMPLETED' ? (
      'Payment was completed'
    ) : status === 'PAYMENT_PENDING_CANCELED' ? (
      'Payment was canceled'
    ) : status === 'ITEM_CANCELED' ? (
      `${getCanceledItemLabel()} was canceled`
    ) : status === 'INVOICE_CANCELED' ? (
      'Invoice was canceled'
    ) : status === 'PAYMENT_EXPIRED' ? (
      'Payment expired'
    ) : status === 'REFUND_REQUESTED' ? (
      'Refund was requested'
    ) : status === 'REFUND_COMPLETED' ? (
      'Refund was completed'
    ) : status === 'REFUND_REJECTED' ? (
      'Refund request was not approved · booking kept'
    ) : status === 'REFUND_FAILED' ? (
      'Refund could not be processed'
    ) : (
      status
    );

  const isIconGreen =
    status === 'INVOICE_CREATED' ||
    status === 'PAYMENT_COMPLETED' ||
    status === 'REFUND_COMPLETED';

  const notShowCancellationNo =
    status === 'INVOICE_CREATED' ||
    status === 'PAYMENT_COMPLETED' ||
    status === 'PAYMENT_EXPIRED' ||
    status === 'REFUND_REQUESTED' ||
    status === 'REFUND_COMPLETED' ||
    status === 'REFUND_REJECTED' ||
    status === 'REFUND_FAILED';

  if (isMobile) {
    return (
      <div className='relative flex items-start gap-3 w-full'>
        {isIconGreen ? (
          <div className='flex size-[30px] justify-center items-center bg-green-deep02 rounded-[50px] shrink-0'>
            <MobileReceiptIcon className='text-icon-inverse' />
          </div>
        ) : (
          <div className='flex size-[30px] justify-center items-center bg-functional-failed02 rounded-[50px] shrink-0'>
            <MobileReceiptCancelIcon className='text-icon-inverse' />
          </div>
        )}

        <div className='flex flex-col w-full min-w-0 gap-0.5'>
          <div className='typo-b14Strong'>{formattedStatus}</div>
          <div className='flex flex-col typo-b12 text-text-secondary/60'>
            {!notShowCancellationNo && (
              <div>Cancellation No. {data.canceledSerialNumber}</div>
            )}
            <div>{formatDateToCustomReadable(data.occurredAt)}</div>
          </div>

          {itemsText && (
            <div className='typo-b12Strong text-text-secondary break-words'>
              {itemsText}
            </div>
          )}

          {isAdmin && data.cancelReason && (
            <div className='typo-b12 text-text-secondary mt-1'>
              {data.cancelReason}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='relative flex items-start gap-4 w-full'>
      {isIconGreen ? (
        <div className='flex size-10 justify-center items-center bg-green-deep02 rounded-[50px]'>
          <ReceiptIcon className='text-icon-inverse' />
        </div>
      ) : (
        <div className='flex size-10 justify-center items-center bg-functional-failed02 rounded-[50px]'>
          <ReceiptCancelIcon className='text-icon-inverse' />
        </div>
      )}

      <div className='flex flex-col max-w-[280px] w-full'>
        <div className='typo-b16Strong'>{formattedStatus}</div>
        <div className='flex flex-col typo-b12 text-text-secondary'>
          {!notShowCancellationNo && (
            <div>Cancellation No. {data.canceledSerialNumber}</div>
          )}
          <div>{formatDateToCustomReadable(data.occurredAt)}</div>
        </div>
      </div>

      <div
        className={`typo-b12 text-text-secondary min-h-6 ${
          isAdmin ? 'max-w-[220px] w-full' : 'flex-1'
        }`}
      >
        {itemsText}
      </div>

      {isAdmin && (
        <div className='typo-b12 text-text-secondary min-h-6 max-w-[160px] w-full'>
          {data.cancelReason}
        </div>
      )}
    </div>
  );
};

export default BillingRow;
